import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PgpAgendaPage from './PgpAgendaPage';
import PgpMappingPage from './PgpMappingPage';
import PgpRosterPage from './PgpRosterPage';
import PgpChartsPage from './PgpChartsPage';
import PgpGeniusPage from './PgpGeniusPage';

/**
 * The PGP publishing suite, ported from the coworker dashboard's /pgp nav.
 *
 * Each view has its own URL, so selecting one is a real navigation: deep links,
 * reloads, the back button and shared links all behave the same way.
 *
 * Mapping and Genius are admin surfaces (full CRUD / pipeline QA), gated on the
 * `pgp_admin` perm. The upstream dashboard has no auth at all, so that gating is
 * new here. Tabs the user cannot open are not rendered, and navigating straight
 * to one still lands on the route guard in App.js.
 *
 * Not yet ported from upstream: Excel / PDF / HTML exports, the pencil-edit
 * modal for deal fields (Mapping edits status / Lead A&R / Action inline),
 * Genius artist search in the add flow, and batch status upload.
 */
const VIEWS = [
  { id: 'agenda', label: 'Agenda', path: '/pgp/agenda', Component: PgpAgendaPage },
  { id: 'charts', label: 'Charts', path: '/pgp/charts', Component: PgpChartsPage },
  { id: 'roster', label: 'Roster', path: '/pgp/roster', Component: PgpRosterPage },
  {
    id: 'mapping',
    label: 'Mapping',
    path: '/pgp/mapping',
    Component: PgpMappingPage,
    perm: 'pgp_admin',
  },
  {
    id: 'genius',
    label: 'Genius',
    path: '/pgp/genius',
    Component: PgpGeniusPage,
    perm: 'pgp_admin',
  },
];

export default function PgpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPerm } = useAuth();

  const views = VIEWS.filter((v) => !v.perm || hasPerm(v.perm));

  // The path is the source of truth: /pgp bare redirects to /pgp/agenda.
  const byPath = views.find((v) => location.pathname.startsWith(v.path));
  const active = byPath?.id || views[0]?.id;

  const setActive = (next) => {
    const view = views.find((v) => v.id === next);
    if (view && view.path !== location.pathname) navigate(view.path);
  };

  const Active = (views.find((v) => v.id === active) || views[0])?.Component;

  if (!Active) return null;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {views.map((view) => (
          <button
            key={view.id}
            type="button"
            onClick={() => setActive(view.id)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              active === view.id
                ? 'bg-violet-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {view.label}
          </button>
        ))}
      </div>
      <Active />
    </div>
  );
}
